// The chips the athlete can tap instead of typing (pure, tested without a
// browser): above the composer from GET /v2/thread/suggestions, the quiet
// thread's own prompts when no state offers any, and body.followups under the
// newest coach message or card. Mobile's lib/v2/suggestions.ts has the same rules.

export const MAX_CHIPS = 3;
/** `chip` on a message: which offer it was. A suggestion sends its own state instead. */
export const CHIP_FOLLOWUP = 'followup';
export const CHIP_QUIET = 'quiet';

/** The route's answer, whatever it was: anything unreadable is no chip. */
export function readSuggestions(data) {
  if (!Array.isArray(data)) return [];
  return data.filter((c) => typeof c?.text === 'string' && c.text.trim()).slice(0, MAX_CHIPS)
    .map((c) => ({ text: c.text, context_ref: c.context_ref || null, why: c.why || null, state: c.state || null }));
}

/** The quiet thread's prompts when no state applies; "today's session" carries that session. */
export function quietPrompts(cal, today) {
  const todays = Object.values(cal?.sessions || {})
    .filter((s) => String(s.scheduled_date).slice(0, 10) === today && Number.isInteger(s.id))
    .sort((a, b) => a.id - b.id)[0];
  const chip = (text, context_ref = null) => ({ text, context_ref, why: null, state: CHIP_QUIET });
  return [
    ...(todays ? [chip('Why is today’s session in my plan?', String(todays.id))] : []),
    chip("How's my week looking?"),
    chip("I'm short on time this week"),
  ];
}

/** The newest coach message, while the athlete hasn't answered it and it stands: only it offers follow-ups. */
export function latestCoachId(items) {
  for (let k = (items?.length || 0) - 1; k >= 0; k -= 1) {
    const { type, state, id } = items[k];
    if (type === 'athlete_message') return null;
    if (type === 'coach_message') return state === 'active' ? String(id) : null;
  }
  return null;
}

/** A coach message's follow-ups as chips. A card's are about the card; a reply's follow the conversation. */
export function followupChips(item) {
  const list = item?.body?.followups;
  if (!Array.isArray(list)) return [];
  const ref = !item.body.kind || item.body.kind === 'chat' ? null : String(item.id);
  return list.filter((t) => typeof t === 'string' && t.trim()).slice(0, MAX_CHIPS)
    .map((text) => ({ text, context_ref: ref, why: null, state: CHIP_FOLLOWUP }));
}

/** What sits above the composer: the states' chips, else the quiet prompts, less any showing under the last reply. */
export function composerChips({ suggestions = [], quiet = false, cal = null, today = '', shown = [] }) {
  const chips = suggestions.length ? suggestions : quiet ? quietPrompts(cal, today) : [];
  const taken = new Set(shown.map((c) => c.text));
  return chips.filter((c) => !taken.has(c.text));
}

const PLACEHOLDER = {
  race_morning: 'Anything before you race?',
  after_race: 'Ask about your race…',
  race_week: 'Ask about race week…',
  restriction: 'Ask what you can still do…',
  key_session: 'Ask about your session…',
  new_athlete: 'Ask what I can see so far…',
  goal_only: 'Ask about your goal…',
  no_goal: 'Ask what to aim for…',
};

/** The composer's hint: what's attached, else the athlete's state; null leaves the composer's own. */
export function composerPlaceholder(context, suggestions = []) {
  const ref = context?.ref ? String(context.ref) : '';
  if (ref.startsWith('activity:')) return 'Ask about this activity…';
  if (ref.startsWith('race:')) return 'Ask about this race…';
  if (ref) return null;
  return PLACEHOLDER[suggestions[0]?.state] || null;
}

/** "About …" for the refs that aren't a thread item or a session; null for those that are. */
export function objectLabel(ref, cal) {
  const [kind, id] = String(ref).split(':');
  if (kind === 'activity' && id) return 'this activity';
  if (kind === 'race' && id) return cal?.races?.[id]?.title || 'your race';
  return null;
}
